import { Injectable } from '@angular/core';
import { CatalogosService } from '../catalogos.service';

/**
 * Service for calculating availability thresholds, colors, and date ranges
 * for quotation availability tracking.
 *
 * Business Rules:
 * - Different thresholds per office (TC CDMX vs QUERETARO)
 * - Different thresholds per unit type and weekday
 * - Color coding: RED (high demand/service too soon), ORANGE (nearing threshold), GREEN (available)
 * - Service must be >36 hours away to avoid RED status
 * - Solicitud aceptada expires after 24 hours
 */
@Injectable()
export class AvailabilityCalculatorService {

    /**
     * Availability thresholds per office, unit type, and weekday
     * Structure: [Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday]
     * "SIEMPRE" = always show red/high demand
     * Loaded dynamically from Firebase Realtime Database (catalogos/limitesDisponibilidad)
     */
    private DISPONIBILIDAD_THRESHOLDS: any = {
        "TC CDMX": {
            "TIPO 2": [15, 15, 15, 15, 15, 25, 25],
            "TIPO 1": [10, 10, 10, 10, 10, 20, 20],
            "TIPO 2 LUJO": [2, 2, 2, 2, 2, 2, 2],
            "AUTOS": [3, 3, 3, 3, 3, 3, 3],
            "CLASE V SIENA": [2, 2, 2, 2, 2, 2, 2],
            "TIPO 3 (21 PAS)": [2, 2, 2, 2, 2, 2, 2],
            "V CLASS LUJO XL": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "SUBURBAN": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"]
        },
        "QUERETARO": {
            "TIPO 2": [2, 2, 2, 2, 2, 2, 2],
            "TIPO 1": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "TIPO 2 LUJO": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "AUTOS": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "CLASE V SIENA": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "TIPO 3 (21 PAS)": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "V CLASS LUJO XL": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"],
            "SUBURBAN": ["SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE", "SIEMPRE"]
        }
    };

    constructor(private catalogosService: CatalogosService) {
        this.loadThresholdsFromFirebase();
    }

    /**
     * Loads availability thresholds from Firebase Realtime Database
     * Path: catalogos/limitesDisponibilidad
     */
    private loadThresholdsFromFirebase(): void {
        this.catalogosService.obtenerLimiteDisponibilidad().valueChanges().take(1).subscribe((data: any) => {
            if (data) {
                this.DISPONIBILIDAD_THRESHOLDS = data
            }
        });
    }

    /**
     * Pads single digit numbers with leading zero
     */
    private pad2(n: number): string {
        return n < 10 ? '0' + n : '' + n;
    }

    /**
     * Converts millisecond timestamp to YYYY-MM-DD date key
     */
    dateKey(ms: number): string {
        const d = new Date(ms);
        const y = d.getFullYear();
        const m = this.pad2(d.getMonth() + 1);
        const day = this.pad2(d.getDate());
        return y + '-' + m + '-' + day;
    }
  
    /**
     * Adjusts JavaScript weekday (0=Sunday) to Monday-first (0=Monday)
     */
    getAdjustedWeekday(weekday: number): number {
        return (weekday + 6) % 7;
    }

    /**
     * Gets availability threshold for office, unit type, and weekday
     * @param office Office name (TC CDMX or QUERETARO)
     * @param tipoUnidad Normalized unit type
     * @param weekday Weekday number (0=Sunday, 6=Saturday)
     * @returns Threshold number or "SIEMPRE"
     */
    getAvailabilityThreshold(office: string, tipoUnidad: string, weekday: number): any {
        const thresholds = this.DISPONIBILIDAD_THRESHOLDS[office];
        if (!thresholds || !thresholds[tipoUnidad]) {
            return null;
        }

        const adjustedWeekday = this.getAdjustedWeekday(weekday);
        return thresholds[tipoUnidad][adjustedWeekday];
    }

    /**
     * Calculates total unit count from results array
     * Each result may have a _unitCount property (number of units of the matching type)
     */
    calculateTotalUnits(results: any[]): number {
        let total = 0;
        for (let i = 0; i < results.length; i++) {
            const unitCount = results[i]._unitCount || 1;
            total += unitCount;
        }
        return total;
    }

    /**
     * Determines if availability should show RED
     * RED when: threshold === "SIEMPRE" OR total units >= threshold
     */
    shouldShowRed(results: any[], weekday: number, office: string, tipoUnidad: string): boolean {
        const threshold = this.getAvailabilityThreshold(office, tipoUnidad, weekday);
        if (!threshold) return false;

        const totalUnits = this.calculateTotalUnits(results);
        const isRed = threshold === "SIEMPRE" || totalUnits >= threshold;

        return isRed;
    }

    /**
     * Determines if availability should show ORANGE
     * ORANGE when: total units >= 80% of threshold AND total units < threshold
     */
    shouldShowOrange(results: any[], weekday: number, office: string, tipoUnidad: string): boolean {
        const threshold = this.getAvailabilityThreshold(office, tipoUnidad, weekday);
        if (!threshold || threshold === "SIEMPRE") return false;

        const totalUnits = this.calculateTotalUnits(results);
        const eightyPercent = Math.floor(threshold * 0.8);
        const isOrange = totalUnits >= eightyPercent && totalUnits < threshold;
        return isOrange;
    }

    /**
     * Calculates the color for service day based on all availability rules
     *
     * Rules (in order):
     * 1. If service is <36 hours away -> RED (Servicio muy próximo)
     * 2. If solicitud accepted (including via compras) -> GREEN
     * 3. If threshold reached -> RED (Demanda alta)
     * 4. If nearing threshold (80%) -> ORANGE
     * 5. Otherwise -> GREEN
     *
     * @returns Object with color and reason
     */
    calculateServiceDayColor(params: {
        dataDisponibilidad: any[];
        indexDiaServicio: number;
        solicitudAceptada: boolean;
        fechaInicioServicio: number;
        office: string;
        tipoUnidad: string;
    }): { color: 'red' | 'orange' | 'green' | null; razon: string | null } {

        const { dataDisponibilidad, indexDiaServicio, solicitudAceptada, fechaInicioServicio, office, tipoUnidad } = params;

        if (!dataDisponibilidad || dataDisponibilidad.length === 0 || indexDiaServicio === -1) {
            return { color: null, razon: null };
        }

        const day = dataDisponibilidad[indexDiaServicio];
        if (!day || !day.results || typeof day.weekday === 'undefined') {
            return { color: null, razon: null };
        }

        const weekday = day.weekday;
        const now = new Date();
        const hoursToService = (fechaInicioServicio - now.getTime()) / (1000 * 60 * 60);

        // Rule 1: Service too soon (<36 hours) - always show red regardless of solicitud
        if (hoursToService <= 36) {
            return { color: 'red', razon: 'Servicio muy próximo' };
        }

        // Rule 2: Solicitud accepted (including via compras) overrides demand check
        if (solicitudAceptada) {
            return { color: 'green', razon: null };
        }

        // Rule 3: Threshold logic
        if (this.shouldShowRed(day.results, weekday, office, tipoUnidad)) {
            return { color: 'red', razon: 'Demanda alta' };
        }

        // Rule 4: Orange if nearing threshold
        if (this.shouldShowOrange(day.results, weekday, office, tipoUnidad)) {
            return { color: 'orange', razon: null };
        }

        return { color: 'green', razon: null };
    }

    /**
     * Evaluates EVERY service day (inicio -> fin) with the per-day rules of
     * calculateServiceDayColor and returns the worst color (red > orange > green).
     *
     * - `razon` keeps the legacy literals ('Servicio muy próximo' / 'Demanda alta'):
     *   they are sent downstream (razonBajaDisponibilidad) and must not change.
     *   Precedence is the same as the single-day logic: muy próximo wins.
     * - `detalle` is UI-only text (one line per reason) listing the days without
     *   availability. It is never persisted.
     * - A solicitud accepted keeps bypassing the demand check on every day, but
     *   never the 'Servicio muy próximo' rule.
     * - Days that are not present in dataDisponibilidad cannot be evaluated and are
     *   skipped; the caller must load a range that covers the whole service.
     */
    calculateServiceRangeColor(params: {
        dataDisponibilidad: any[];
        solicitudAceptada: boolean;
        fechaInicioServicio: number;
        fechaFinServicio: number;
        office: string;
        tipoUnidad: string;
    }): {
        color: 'red' | 'orange' | 'green' | null;
        razon: string | null;
        detalle: string[];
        diasSinDisponibilidad: string[];
        diasDisponibilidadLimitada: string[];
    } {
        const { dataDisponibilidad, solicitudAceptada, fechaInicioServicio, office, tipoUnidad } = params;
        const empty = {
            color: null as any,
            razon: null as string | null,
            detalle: [] as string[],
            diasSinDisponibilidad: [] as string[],
            diasDisponibilidadLimitada: [] as string[]
        };

        if (!dataDisponibilidad || dataDisponibilidad.length === 0 || !fechaInicioServicio) {
            return empty;
        }

        const indexByDate: any = {};
        for (let i = 0; i < dataDisponibilidad.length; i++) {
            if (dataDisponibilidad[i] && dataDisponibilidad[i].date) {
                indexByDate[dataDisponibilidad[i].date] = i;
            }
        }

        const dayMs = 24 * 60 * 60 * 1000;
        const inicio = new Date(fechaInicioServicio);
        const startDayMs = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()).getTime();
        let endDayMs = startDayMs;
        if (params.fechaFinServicio && params.fechaFinServicio > fechaInicioServicio) {
            const fin = new Date(params.fechaFinServicio);
            endDayMs = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate()).getTime();
        }

        // The start day must be evaluable, same as the single-day behaviour
        const startKey = this.dateKey(startDayMs);
        if (typeof indexByDate[startKey] === 'undefined') {
            return empty;
        }

        let hayRojo = false;
        let hayNaranja = false;
        let evaluados = 0;
        const servicioMuyProximo = (fechaInicioServicio - Date.now()) / (1000 * 60 * 60) <= 36;
        const diasRojos: string[] = [];
        const diasNaranjas: string[] = [];

        // Iterate by calendar day (setDate keeps it DST-safe, unlike += 24h)
        for (let d = new Date(startDayMs); d.getTime() <= endDayMs; d.setDate(d.getDate() + 1)) {
            const key = this.dateKey(d.getTime());
            const idx = indexByDate[key];
            if (typeof idx === 'undefined') {
                continue;
            }
            // Rule 1 depends only on the service start, so it is evaluated once for all days;
            // demand is still checked per day so every reason is listed.
            const res = this.calculateServiceDayColor({
                dataDisponibilidad: dataDisponibilidad,
                indexDiaServicio: idx,
                solicitudAceptada: solicitudAceptada,
                fechaInicioServicio: servicioMuyProximo ? Number.MAX_VALUE : fechaInicioServicio,
                office: office,
                tipoUnidad: tipoUnidad
            });
            if (!res.color) {
                continue;
            }
            evaluados++;
            if (res.color === 'red') {
                hayRojo = true;
                diasRojos.push(key);
            } else if (res.color === 'orange') {
                hayNaranja = true;
                diasNaranjas.push(key);
            }
        }

        if (evaluados === 0) {
            return empty;
        }

        const detalle: string[] = [];
        if (servicioMuyProximo) {
            detalle.push('Servicio muy próximo (menos de 36 horas): requiere solicitud de disponibilidad.');
        }
        if (diasRojos.length > 0) {
            detalle.push('Sin disponibilidad los días: ' + this.formatDayKeys(diasRojos) + '.');
        }
        if (diasNaranjas.length > 0) {
            detalle.push('Disponibilidad limitada los días: ' + this.formatDayKeys(diasNaranjas) + '.');
        }

        let color: 'red' | 'orange' | 'green' = 'green';
        let razon: string | null = null;
        if (hayRojo || servicioMuyProximo) {
            color = 'red';
            razon = servicioMuyProximo ? 'Servicio muy próximo' : 'Demanda alta';
        } else if (hayNaranja) {
            color = 'orange';
        }

        return {
            color: color,
            razon: razon,
            detalle: detalle,
            diasSinDisponibilidad: diasRojos,
            diasDisponibilidadLimitada: diasNaranjas
        };
    }

    /**
     * TR-21: demand-only color for a set of calendar days (the days ADDED by an extension).
     *
     * Unlike calculateServiceRangeColor it never applies the "Servicio muy próximo" (<36 h)
     * rule: that rule depends on when the service starts, and an extension of a service that
     * is already running (or starts in a few hours) must be judged only by the demand on the
     * added days. A solicitud accepted is not considered either: the extension is a new change.
     *
     * - red    -> at least one day reached the threshold ("SIEMPRE" counts as red)
     * - null   -> no day is red and at least one day could not be evaluated (not in
     *             dataDisponibilidad): "could not verify"
     * - orange -> no red, every day evaluated, at least one is orange
     * - green  -> every day evaluated and available
     * `diasRojos` are YYYY-MM-DD keys, in the order of `dayStartsMs`.
     */
    calculateDaysDemandColor(params: {
        dataDisponibilidad: any[];
        dayStartsMs: number[];
        office: string;
        tipoUnidad: string;
    }): { color: 'red' | 'orange' | 'green' | null; diasRojos: string[]; diasNaranjas: string[] } {
        const { dataDisponibilidad, dayStartsMs, office, tipoUnidad } = params;
        const result = { color: null as any, diasRojos: [] as string[], diasNaranjas: [] as string[] };

        if (!dataDisponibilidad || dataDisponibilidad.length === 0 || !dayStartsMs || dayStartsMs.length === 0) {
            return result;
        }

        const indexByDate: any = {};
        for (let i = 0; i < dataDisponibilidad.length; i++) {
            if (dataDisponibilidad[i] && dataDisponibilidad[i].date) {
                indexByDate[dataDisponibilidad[i].date] = i;
            }
        }

        let sinEvaluar = 0;
        for (let j = 0; j < dayStartsMs.length; j++) {
            const key = this.dateKey(dayStartsMs[j]);
            const idx = indexByDate[key];
            if (typeof idx === 'undefined') {
                sinEvaluar++;
                continue;
            }
            // No threshold for office/type/weekday: calculateServiceDayColor would answer green
            // (nothing to compare against). For the extension rule that is the unsafe direction,
            // so the day counts as "could not verify" instead.
            const day = dataDisponibilidad[idx];
            if (!tipoUnidad || !day || typeof day.weekday === 'undefined' ||
                !this.getAvailabilityThreshold(office, tipoUnidad, day.weekday)) {
                sinEvaluar++;
                continue;
            }
            const res = this.calculateServiceDayColor({
                dataDisponibilidad: dataDisponibilidad,
                indexDiaServicio: idx,
                solicitudAceptada: false,
                fechaInicioServicio: Number.MAX_VALUE,
                office: office,
                tipoUnidad: tipoUnidad
            });
            if (!res.color) {
                sinEvaluar++;
            } else if (res.color === 'red') {
                result.diasRojos.push(key);
            } else if (res.color === 'orange') {
                result.diasNaranjas.push(key);
            }
        }

        if (result.diasRojos.length > 0) {
            result.color = 'red';
        } else if (sinEvaluar > 0) {
            result.color = null;
        } else if (result.diasNaranjas.length > 0) {
            result.color = 'orange';
        } else {
            result.color = 'green';
        }
        return result;
    }

    /**
     * TR-21: services to count when judging the NEW dates/units of the service being edited.
     * The stored copy of the service (same `$key`) is dropped and the edited version is added
     * instead, so its own units count on every day of the new range (and are not counted twice on
     * days it already occupied).
     */
    withEditedService(servicios: any[], selfKey: string, edited: any): any[] {
        const out: any[] = [];
        for (let i = 0; i < (servicios || []).length; i++) {
            if (!selfKey || servicios[i].$key !== selfKey) {
                out.push(servicios[i]);
            }
        }
        if (edited) {
            out.push(edited);
        }
        return out;
    }

    /**
     * Formats YYYY-MM-DD keys as "dd/MM/yyyy" for UI messages
     */
    formatDayKeys(keys: string[]): string {
        const out: string[] = [];
        for (let i = 0; i < keys.length; i++) {
            const parts = keys[i].split('-');
            out.push(parts[2] + '/' + parts[1] + '/' + parts[0]);
        }
        return out.join(', ');
    }

    /**
     * Formats remaining time until solicitud expires (24 hours from acceptance)
     * @param fecha_aceptacion Timestamp when solicitud was accepted
     * @returns Formatted string like "1d 5h 30m" or "Caducada"
     */
    getRemainingTime(fecha_aceptacion: number): string {
        if (!fecha_aceptacion) {
            return 'Caducada';
        }

        const end = new Date(fecha_aceptacion);
        const now = new Date();
        const _second = 1000;
        const _minute = _second * 60;
        const _hour = _minute * 60;
        const _day = _hour * 24;
        const fechaExpiracion = end.getTime() + _day;

        const distance = fechaExpiracion - now.getTime();
        if (distance < 0) {
            return 'Caducada';
        }

        const days = Math.floor(distance / _day);
        const hours = Math.floor((distance % _day) / _hour);
        const minutes = Math.floor((distance % _hour) / _minute);
        const seconds = Math.floor((distance % _minute) / _second);

        let remainingTime = '';
        if (days > 0)
            remainingTime += days + 'd ';
        if (hours > 0)
            remainingTime += hours + 'h ';
        if (minutes > 0)
            remainingTime += minutes + 'm ';
        if (remainingTime === '') {
            remainingTime = seconds + 's';
        }
        return remainingTime;
    }

    /**
     * Calculates date range boundaries for availability search
     * Returns timestamps for start (00:00:00) and end (23:59:59.999) of range
     */
    calculateDateRangeBoundaries(centerDate: Date, daysBeforeAndAfter: number, daysAfter?: number): { from: number; to: number } {
        const dayMs = 24 * 60 * 60 * 1000;
        const after = typeof daysAfter === 'number' ? daysAfter : daysBeforeAndAfter;
        const center = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate()).getTime();
        const from = center - daysBeforeAndAfter * dayMs;
        const to = center + after * dayMs + (dayMs - 1);

        return { from, to };
    }

    /**
     * Build empty date buckets for availability calendar
     * @param centerDate Center date for the range
     * @param daysBeforeAndAfter Days before center (and after, unless daysAfter is given)
     * @param daysAfter Optional days after center (to cover multi-day services)
     * @returns Object with date keys and empty bucket structure
     */
    buildDateBuckets(centerDate: Date, daysBeforeAndAfter: number, daysAfter?: number): any {
        const buckets: any = {};
        const after = typeof daysAfter === 'number' ? daysAfter : daysBeforeAndAfter;

        for (let i = -daysBeforeAndAfter; i <= after; i++) {
            // calendar-day arithmetic (DST-safe)
            const dMs = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate() + i).getTime();
            const key = this.dateKey(dMs);
            const weekday = new Date(dMs).getDay();
            buckets[key] = { weekday: weekday, date: key, results: [] };
        }

        return buckets;
    }

    /**
     * Categorize servicios into date buckets, filtering by zone and unit type
     * @param buckets Date buckets to fill
     * @param servicios Array of servicios to categorize
     * @param zonaCotizacion Zone filter (null = no filter)
     * @param selectedTipo Selected unit type to match
     * @param unitTypeMapper UnitTypeMapperService instance for matching
     * @returns Updated buckets with servicios categorized
     */
    categorizeServiciosIntoBuckets(
        buckets: any,
        servicios: any[],
        zonaCotizacion: string | null,
        selectedTipo: string,
        unitTypeMapper: any,
        office?: string
    ): any {
        for (let idx = 0; idx < servicios.length; idx++) {
            const svc = servicios[idx];
            const finDeServicio = svc.finDeServicio || svc.inicioDeServicio;

            if (office) {
                const oficinaDeOrigen = String(svc.oficinaDeOrigen);
                let shouldInclude = true;

                if (office === 'QUERETARO') {
                    shouldInclude = oficinaDeOrigen === '18';
                } else if (office === 'TC CDMX') {
                    shouldInclude = oficinaDeOrigen !== '18';
                }

                if (!shouldInclude) {
                    continue;
                }
            }

            const unitCount = unitTypeMapper.countMatchingUnits(svc.unidadesSolicitadas, selectedTipo);

            if (unitCount > 0) {
                const servicioWithCount = Object.assign({}, svc, { _unitCount: unitCount });

                const dayMs = 24 * 60 * 60 * 1000;
                const startDate = new Date(svc.inicioDeServicio);
                const endDate = new Date(finDeServicio);

                const startDayMs = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
                const endDayMs = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();

                for (let dayTime = startDayMs; dayTime <= endDayMs; dayTime += dayMs) {
                    const key = this.dateKey(dayTime);
                    if (buckets[key]) {
                        buckets[key].results.push(servicioWithCount);
                    }
                }
            }
        }

        return buckets;
    }

    /**
     * Convert buckets object to sorted array
     * @param buckets Buckets object with date keys
     * @returns Sorted array of buckets
     */
    bucketsToSortedArray(buckets: any): any[] {
        const keys = [];
        for (const k in buckets) {
            if (buckets.hasOwnProperty(k)) {
                keys.push(k);
            }
        }
        keys.sort();

        const dataArray = [];
        for (let i = 0; i < keys.length; i++) {
            dataArray.push(buckets[keys[i]]);
        }

        return dataArray;
    }

    /**
     * Add blank days at the beginning for calendar display alignment
     * @param dataDisponibilidad Array of availability data
     * @returns Object with updated array, blank days count, and service day index
     */
    addBlankDaysForCalendar(dataDisponibilidad: any[], daysBeforeAndAfter: number): {
        data: any[];
        blankDaysToAdd: number;
        indexDiaServicio: number;
    } {
        let firstWeekday = 0;
        if (dataDisponibilidad.length > 0 &&
            typeof dataDisponibilidad[0].weekday !== 'undefined' &&
            dataDisponibilidad[0].weekday !== null) {
            firstWeekday = dataDisponibilidad[0].weekday / 1;
        }

        const blankDaysToAdd = firstWeekday;
        const updatedData = [...dataDisponibilidad];

        for (let i = 0; i < blankDaysToAdd; i++) {
            updatedData.unshift({ weekday: '', date: '', results: [] });
        }

        const indexDiaServicio = daysBeforeAndAfter + blankDaysToAdd;

        return {
            data: updatedData,
            blankDaysToAdd,
            indexDiaServicio
        };
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/availability-calculator.service.ts